import React, { useCallback, useEffect, useRef, useState } from 'react';
import Avatar from '../../atoms/Avatar';
import {
  PEOPLE_PICKER_AVATAR_PX,
  PEOPLE_PICKER_MAX_VISIBLE_AVATARS,
} from './PeoplePicker.constants';
import {
  AvatarStack,
  Caret,
  CheckMark,
  Dropdown,
  EmptyRow,
  Overflow,
  PersonName,
  PersonRow,
  Placeholder,
  PickerWrap,
  SelectedLabel,
  TriggerBtn,
} from './PeoplePicker.styled';

/** A selectable person entry. Only `id` and `name` are required. */
export interface PeoplePickerPerson {
  readonly id: string;
  readonly name: string;
}

export interface PeoplePickerProps {
  /** All available people to choose from. */
  readonly people: readonly PeoplePickerPerson[];
  /** Currently selected person IDs. */
  readonly selectedIds: readonly string[];
  /** Called when the selection changes. */
  readonly onChange: (next: string[]) => void;
  /** Disables the picker when true. */
  readonly disabled?: boolean;
  /** Placeholder text when no one is selected. */
  readonly placeholder?: string;
  /**
   * Label rendered with the regular {@code SelectedLabel} style (not the dim
   * placeholder) when no one is selected. Use this when "no selection" is a
   * meaningful value with its own semantics — e.g. "Everyone in your
   * organization" — and you want it to read as such rather than as an
   * unfilled field. When provided, takes precedence over {@code placeholder}.
   */
  readonly emptySelectionLabel?: string;
  /** Max avatars to show in the collapsed trigger before showing "+N". */
  readonly maxVisibleAvatars?: number;
  /** Text to show when the people list is empty. */
  readonly emptyText?: string;
  readonly dataTestId?: string;
}

const PeoplePicker: React.FC<PeoplePickerProps> = ({
  people,
  selectedIds,
  onChange,
  disabled = false,
  placeholder = 'Select people…',
  emptySelectionLabel,
  maxVisibleAvatars = PEOPLE_PICKER_MAX_VISIBLE_AVATARS,
  emptyText = 'No people available',
  dataTestId = 'people-picker',
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      if (
        wrapRef.current &&
        e.target instanceof Node &&
        !wrapRef.current.contains(e.target)
      )
        setOpen(false);
    };
    document.addEventListener('pointerdown', close, true);
    return () => document.removeEventListener('pointerdown', close, true);
  }, [open]);

  const toggle = useCallback(
    (id: string) => {
      if (disabled) return;
      const set = new Set(selectedIds);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      onChange([...set]);
    },
    [disabled, selectedIds, onChange],
  );

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  const idSet = new Set(selectedIds);
  const selectedPeople = people.filter((p) => idSet.has(p.id));
  const visibleAvatars = selectedPeople.slice(0, maxVisibleAvatars);
  const overflow = selectedPeople.length - maxVisibleAvatars;

  const summaryText =
    selectedPeople.length === 0
      ? undefined
      : selectedPeople.length === 1
        ? selectedPeople[0].name
        : `${selectedPeople.length} selected`;

  return (
    <PickerWrap ref={wrapRef}>
      <TriggerBtn
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={dataTestId}
        disabled={disabled}
        onPointerDown={stop}
        onClick={(e) => {
          stop(e);
          if (!disabled) setOpen((o) => !o);
        }}
      >
        {visibleAvatars.length > 0 && (
          <AvatarStack data-testid={`${dataTestId}__avatars`}>
            {visibleAvatars.map((p) => (
              <Avatar
                key={p.id}
                name={p.name}
                size={PEOPLE_PICKER_AVATAR_PX}
                dataTestId={`${dataTestId}__avatar-${p.id}`}
              />
            ))}
          </AvatarStack>
        )}
        {summaryText ? (
          <SelectedLabel>{summaryText}</SelectedLabel>
        ) : emptySelectionLabel ? (
          <SelectedLabel data-testid={`${dataTestId}__empty-label`}>
            {emptySelectionLabel}
          </SelectedLabel>
        ) : (
          <Placeholder>{placeholder}</Placeholder>
        )}
        {overflow > 0 && <Overflow>+{overflow}</Overflow>}
        <Caret className="material-symbols-rounded" aria-hidden="true">
          expand_more
        </Caret>
      </TriggerBtn>
      {open && (
        <Dropdown role="listbox" aria-multiselectable onPointerDown={stop}>
          {people.map((p) => {
            const checked = idSet.has(p.id);
            return (
              <PersonRow
                key={p.id}
                type="button"
                role="option"
                aria-selected={checked}
                $selected={checked}
                onClick={(e) => {
                  stop(e);
                  toggle(p.id);
                }}
                data-testid={`${dataTestId}__option-${p.id}`}
              >
                <Avatar name={p.name} size={PEOPLE_PICKER_AVATAR_PX} />
                <PersonName>{p.name}</PersonName>
                {checked && (
                  <CheckMark
                    className="material-symbols-rounded"
                    aria-hidden="true"
                  >
                    check
                  </CheckMark>
                )}
              </PersonRow>
            );
          })}
          {people.length === 0 && <EmptyRow>{emptyText}</EmptyRow>}
        </Dropdown>
      )}
    </PickerWrap>
  );
};

export default PeoplePicker;
